import { ALK_ROLES } from '../../../models/alk-roles';
import { FLEET_MANAGER, FLEET_SUPPORT } from '../../../models/fleet-roles';
import {
  companySettingsSnapshot,
  partnerSettingsSnapshot,
  isFleetPortalOnly,
} from '../../../repositories/app-settings/app-settings.repository';
import { isFeatureEnabled } from '../../../repositories/feature-flags/feature-flags.repository';
import { composeRoles } from '../../../utils/compose-roles';
import { boolDefinedAndTrue } from '../../../utils/bool-defined-and-true';
import { setSecondaryNavOptions } from '../../../components/secondary-nav/repositories/secondary-nav.actions';
import { SecondaryNavKey } from '../../../components/secondary-nav/models/secondary-nav-key';
import { accountSummarySnapshot } from '../../../repositories/ams/ams.repository';
import { dispatch } from '@ngneat/effects';
import { translate } from '@jsverse/transloco';
import { getCurrentAuthenticatedUser } from '@tmx/auth';

export function initSecondaryNavCfs() {
  const companySettings = companySettingsSnapshot();
  const partnerSettings = partnerSettingsSnapshot();
  const { products } = accountSummarySnapshot();

  const roles = getCurrentAuthenticatedUser().roles;
  const isNonAlkFleetUser =
    roles.every((role) => !ALK_ROLES.has(role)) &&
    roles.some((role) => role === 'Fleet User');

  const hasNonFleetUserAndHigherRole = composeRoles(
    ALK_ROLES,
    FLEET_MANAGER,
    FLEET_SUPPORT
  );

  const hasHos = products.some(
    (p) => p.addons?.some((a) => a === 'HOS') ?? false
  );

  dispatch(
    setSecondaryNavOptions({
      key: SecondaryNavKey.Cfs,
      options: [
        {
          url: 'cfs/vehicles',
          displayName: translate('cfs.cfs.vehicles'),
          disabled:
            !companySettings.activities.viewVehicles ||
            (partnerSettings.integrationType === 'DriverId' &&
              !isFleetPortalOnly()) ||
            isNonAlkFleetUser,
        },
        {
          url: 'cfs/vehicle-groups',
          displayName: translate('cfs.cfs.vehicleGroups'),
          disabled:
            !companySettings.activities.viewVehicleGroups ||
            (partnerSettings.integrationType === 'DriverId' &&
              !isFleetPortalOnly()),
        },
        {
          url: 'cfs/routing-profiles',
          displayName: translate('cfs.cfs.vehicleRoutingProfiles'),
          disabled:
            !companySettings.isRoutingProfileEnabled ||
            !companySettings.activities.accessRoutingProfiles,
        },
        {
          url: 'cfs/configuration-profiles',
          displayName: translate('cfs.cfs.configurationProfiles'),
          disabled:
            !companySettings.isConfigurationProfileEnabled ||
            !companySettings.activities.accessConfigurationProfiles,
        },
        {
          url: 'cfs/map-sets',
          displayName: translate('cfs.cfs.mapSets'),
          disabled: !companySettings.isMapsetsEnabled,
        },
        {
          url: 'cfs/map-updates',
          displayName: translate('cfs.cfs.mapUpdates'),
          disabled: !companySettings.enableMapManagement,
        },
        {
          url: 'cfs/route-modifiers',
          displayName: translate('cfs.cfs.routeModifiers'),
        },
        {
          url: 'cfs/custom-places',
          displayName: translate('cfs.cfs.customPlaces'),
        },
        {
          url: 'cfs/drivers',
          displayName: translate('cfs.cfs.drivers'),
          disabled:
            !companySettings.activities.viewDrivers || isNonAlkFleetUser,
        },
        {
          url: 'cfs/driver-groups',
          displayName: translate('cfs.cfs.driverGroups'),
          disabled: !companySettings.activities.viewDriverGroups,
        },
        {
          url: 'cfs/speed-coaching-profiles',
          displayName: translate('cfs.cfs.speedCoachingProfiles'),
          disabled: !companySettings.isSpeedCoachingEnabled,
        },
        {
          url: 'cfs/fleet-preferences',
          displayName: translate('cfs.cfs.fleetPreferences'),
          disabled: !(
            isFeatureEnabled('fleet-preferences') &&
            hasHos &&
            hasNonFleetUserAndHigherRole(roles)
          ),
        },
      ].filter((option) => !boolDefinedAndTrue(option.disabled)),
    })
  );
}
